<template>
  <div class="task-card-wrap" :class="{ compact }">
   <!-- Table row — used by the Tasks board (aligned columns under the group header). -->
   <div
     v-if="table"
     class="task-row tbl"
     :class="['urgency-' + task.urgency, { done: task.status === 'done' }]"
     role="button"
     tabindex="0"
     @click="$emit('open', task)"
     @keydown.enter="$emit('open', task)"
   >
     <div class="tc tc-subject">
       <!-- Delete — team only, never on a completed/archived task. Confirms first. -->
       <button
         v-if="isInternal && task.status !== 'done' && !task.archived_at"
         class="tc-del"
         :title="t('tasks.deleteTask')"
         @click.stop="confirmDelete"
       >
         <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
       </button>
       <span class="tc-title">{{ task.title }}</span>
       <span v-if="task.subtask_total" class="tc-sub">{{ task.subtask_done }}/{{ task.subtask_total }}</span>
       <svg v-if="task.visibility === 'private'" class="tc-lock" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
       <span v-if="task.due_at" class="tc-due" :class="{ overdue: isOverdue }">{{ formatDate(task.due_at) }}</span>
     </div>

     <!-- Middle columns rendered in the project's column order — identical order to
          the header, so cells always sit under their header (no CSS-order drift). -->
     <template v-for="key in columns" :key="key">
       <div v-if="key === 'time'" class="tc tc-time" @click.stop>
         <!-- Externals see the tracked time read-only (no start/stop controls). -->
         <TaskTimer
           :task-id="task.id"
           :timer-seconds="task.timer_seconds"
           :timer-started-at="task.timer_started_at"
           :readonly="isExternal"
           @update="onTimerUpdate"
         />
       </div>
       <div v-else-if="key === 'urgency'" class="tc tc-urgency">
         <span class="u-pill" :class="'u-' + task.urgency">{{ t('urgency.' + task.urgency) }}</span>
       </div>
       <div v-else-if="key === 'assignee'" class="tc tc-assignee" @click.stop>
         <button v-if="!isExternal" class="assign-btn" :class="{ unassigned: !assigneeList.length, 'has-stack': assigneeList.length }" :title="assigneeTitle" @click.stop="toggleAssign($event)">
           <span v-if="assigneeList.length" class="avatar-stack">
             <UserAvatar v-for="a in assigneeList.slice(0, 3)" :key="a.id" :user="a" :size="24" :stroke="true" class="av" />
             <span v-if="assigneeList.length > 3" class="av av-more">+{{ assigneeList.length - 3 }}</span>
           </span>
           <svg v-else width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>
         </button>
       </div>
       <div
         v-else-if="fieldOf(key)"
         class="tc tc-field"
         :class="{ 'has-fill': !!fieldFill(fieldOf(key)) }"
         :style="fieldCellStyle(fieldOf(key))"
         @click.stop
       >
         <select
           v-if="fieldOf(key).type === 'select'"
           class="field-input field-select"
           :value="fieldVal(fieldOf(key))"
           @change="onFieldChange(fieldOf(key), $event.target.value)"
         >
           <option value="">{{ t('fields.pickValue') }}</option>
           <option v-for="o in optionsOf(fieldOf(key))" :key="o.value" :value="o.value">{{ o.value }}</option>
         </select>
         <input
           v-else
           :type="fieldOf(key).type === 'number' ? 'number' : (fieldOf(key).type === 'date' ? 'date' : 'text')"
           class="field-input"
           :value="fieldVal(fieldOf(key))"
           @change="onFieldChange(fieldOf(key), $event.target.value)"
         />
       </div>
     </template>

     <div class="tc tc-action" @click.stop>
       <!-- Externals can't complete/reopen — show the status as a pill instead. -->
       <span v-if="isExternal" :class="['badge', 'badge-' + task.status]">{{ t('status.' + task.status) }}</span>
       <button v-else-if="task.status !== 'done' && !task.archived_at" class="done-pill" :title="t('tasks.completeArchives')" @click.stop="$emit('complete', task)">
         <span class="done-circle"></span>{{ t('taskCard.finish') }}
       </button>
       <button v-else-if="isInternal && task.archived_at" class="done-pill restore" @click.stop="$emit('restore', task)">
         <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 102.13-9.36L1 10"/></svg>{{ t('tasks.restore') }}
       </button>
       <button v-else-if="isInternal" class="done-pill restore" @click.stop="$emit('reopen', task)">
         <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 102.13-9.36L1 10"/></svg>{{ t('taskCard.reopen') }}
       </button>
     </div>
   </div>

   <!-- Flat row — dashboard widgets / compact layout. -->
   <div
     v-else
     class="task-row clickable"
     :class="['urgency-' + task.urgency, { done: task.status === 'done' }]"
     role="button"
     tabindex="0"
     @click="$emit('open', task)"
     @keydown.enter="$emit('open', task)"
   >
    <span class="row-tick"></span>

    <div class="row-main">
      <div class="row-title">{{ task.title }}</div>
      <div class="row-meta">
        <!-- Project name intentionally omitted here — it's shown once in the group box header. -->
        <span>{{ t('urgency.' + task.urgency) }}</span>
        <template v-if="task.visibility === 'private'">
          <span class="sep">·</span>
          <span class="priv">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
            {{ t('taskCard.private') }}
          </span>
        </template>
        <template v-if="task.due_at">
          <span class="sep">·</span>
          <span :class="{ overdue: isOverdue }">{{ formatDate(task.due_at) }}</span>
        </template>
      </div>
    </div>

    <div class="row-actions">
      <TaskTimer
        v-if="!isExternal"
        :task-id="task.id"
        :timer-seconds="task.timer_seconds"
        :timer-started-at="task.timer_started_at"
        @update="onTimerUpdate"
      />
      <button
        v-if="task.status !== 'done' && !task.archived_at"
        class="btn btn-sm btn-icon"
        :title="t('taskCard.markDone') + ' — ' + t('tasks.completeArchives')"
        @click.stop="$emit('complete', task)"
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>
      </button>
      <button
        v-else-if="isInternal && task.archived_at"
        class="btn btn-sm btn-icon"
        :title="t('tasks.restore')"
        @click.stop="$emit('restore', task)"
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 102.13-9.36L1 10"/></svg>
      </button>
      <button
        v-else-if="isInternal"
        class="btn btn-sm btn-icon"
        :title="t('taskCard.reopen')"
        @click.stop="$emit('reopen', task)"
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 102.13-9.36L1 10"/></svg>
      </button>
    </div>

    <span :class="['badge', 'badge-' + task.status]">{{ statusLabel }}</span>

    <!-- Quick-assign a developer without opening the task. -->
    <div v-if="!isExternal" class="row-assign" @click.stop>
      <button
        class="assign-btn"
        :class="{ unassigned: !assigneeList.length, 'has-stack': assigneeList.length }"
        :title="assigneeTitle"
        @click.stop="toggleAssign($event)"
      >
        <span v-if="assigneeList.length" class="avatar-stack">
          <UserAvatar v-for="a in assigneeList.slice(0, 3)" :key="a.id" :user="a" :size="24" :stroke="true" class="av" />
          <span v-if="assigneeList.length > 3" class="av av-more">+{{ assigneeList.length - 3 }}</span>
        </span>
        <svg v-else width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>
      </button>
    </div>
    <span v-else-if="assigneeList.length" class="avatar-stack" :title="assigneeTitle">
      <UserAvatar v-for="a in assigneeList.slice(0, 3)" :key="a.id" :user="a" :size="24" :stroke="true" class="av" />
      <span v-if="assigneeList.length > 3" class="av av-more">+{{ assigneeList.length - 3 }}</span>
    </span>

    <span class="row-fill"></span>
   </div>

   <!-- Quick-assign — a small centered modal box, teleported to <body> so it's
        never clipped by a card's overflow or hidden behind sibling rows. -->
   <Teleport to="body">
     <div v-if="assignOpen" class="assign-modal-root" @click.self="assignOpen = false">
       <div class="assign-box" role="dialog" aria-modal="true">
         <div class="assign-box-head">
           <span class="assign-box-title">{{ t('taskForm.assignTo') }}</span>
           <button class="assign-x" :aria-label="t('drawer.close')" @click="assignOpen = false">
             <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
           </button>
         </div>
         <div class="assign-box-body">
           <button v-for="u in users" :key="u.id" class="assign-opt" :class="{ active: selectedIds.has(u.id) }" @click="toggleAssignUser(u.id)">
             <UserAvatar :user="u" :size="24" />
             <span class="ao-name">{{ u.name }}</span>
             <svg v-if="selectedIds.has(u.id)" class="ao-check" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"><polyline points="20 6 9 17 4 12"/></svg>
           </button>
           <p v-if="!users.length" class="ao-empty">—</p>
         </div>
       </div>
     </div>
   </Teleport>
  </div>
</template>

<script>
import { computed, ref } from 'vue'
import TaskTimer from '@/components/TaskTimer.vue'
import UserAvatar from '@/components/UserAvatar.vue'
import { useAuth } from '@/composables/useAuth'
import { useI18n } from '@/composables/useI18n'

const URGENCY_COLORS = { low: '#A8A192', normal: '#0F6E59', high: '#A66E08', critical: '#B8432B' }

function initialsOf(name) {
  return (name || '').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
}

export default {
  name: 'TaskCard',
  components: { TaskTimer, UserAvatar },
  props: {
    task: { type: Object, required: true },
    // Office members for the inline quick-assign dropdown (internal users only).
    users: { type: Array, default: () => [] },
    // Forces the stacked/wrapped layout regardless of viewport — used inside the
    // narrow dashboard widgets where the dense single-row layout would overlap.
    compact: { type: Boolean, default: false },
    // Renders the row as an aligned table row (Tasks board); columns are driven by
    // the --tt-cols variable set on the surrounding group table.
    table: { type: Boolean, default: false },
    // Custom project field columns to render in table mode (after assignee).
    fields: { type: Array, default: () => [] },
    // Ordered list of middle-column keys ('time','urgency','assignee', field ids).
    // Drives each cell's CSS `order` so the row reflows to the project's column order.
    columns: { type: Array, default: () => [] },
  },
  emits: ['open', 'complete', 'reopen', 'restore', 'assign', 'timer-update', 'field-value', 'delete'],
  setup(props, { emit }) {
    const { isManager: _isManager, isExternalClient, isInternal: _isInternal } = useAuth()
    const { t, locale } = useI18n()

    const isExternal   = computed(() => isExternalClient())
    const isManager    = computed(() => _isManager())
    const isInternal   = computed(() => _isInternal())
    const urgencyColor = computed(() => URGENCY_COLORS[props.task.urgency] || '#76B900')
    const statusLabel  = computed(() => t('status.' + props.task.status))
    const isOverdue    = computed(() => {
      if (!props.task.due_at || props.task.status === 'done') return false
      return new Date(props.task.due_at) < new Date()
    })
    // Full assignee set, falling back to the legacy single assignee when the
    // task predates the join-table backfill.
    const assigneeList = computed(() => {
      if (Array.isArray(props.task.assignees) && props.task.assignees.length) return props.task.assignees
      if (props.task.assignee_name) return [{ id: props.task.assigned_to, name: props.task.assignee_name }]
      return []
    })
    const assigneeTitle = computed(() => assigneeList.value.map(a => a.name).join(', ') || t('taskForm.assignTo'))
    const selectedIds   = computed(() => new Set(assigneeList.value.map(a => a.id)))

    // ── inline quick-assign (centered modal box, teleported to <body>) ──
    const assignOpen = ref(false)
    function toggleAssign() {
      assignOpen.value = !assignOpen.value
    }
    // Toggle a single user in/out of the assignee set; the popover stays open so
    // several people can be added at once.
    function toggleAssignUser(userId) {
      const ids = new Set(assigneeList.value.map(a => a.id))
      if (ids.has(userId)) ids.delete(userId)
      else ids.add(userId)
      emit('assign', { task: props.task, userIds: [...ids] })
    }

    function formatDate(dt) {
      if (!dt) return ''
      const loc = locale.value === 'he' ? 'he-IL' : 'en-GB'
      return new Date(dt).toLocaleDateString(loc, { day: '2-digit', month: 'short', year: 'numeric' })
    }

    function onTimerUpdate(payload) {
      emit('timer-update', { taskId: props.task.id, ...payload })
    }

    // Native confirm per product rule: deleting always prompts first.
    function confirmDelete() {
      if (confirm(t('tasks.deleteConfirm', { title: props.task.title }))) {
        emit('delete', props.task)
      }
    }

    function fieldVal(f) {
      const v = props.task.field_values && props.task.field_values[f.id]
      return v == null ? '' : v
    }
    function onFieldChange(f, value) {
      emit('field-value', { task: props.task, fieldId: f.id, value })
    }

    // Resolve a column key to its custom-field object (built-in keys return null).
    function fieldOf(key) {
      return props.fields.find(f => f.id === key) || null
    }
    // Normalise options to {value, color} — the API sends the new shape but older
    // rows still hold plain strings until the next round-trip through the editor.
    function optionsOf(f) {
      return (f.options || []).map(o => typeof o === 'string' ? { value: o, color: null } : o)
    }
    // Monday-style fill: for a select cell, the picked option's colour wins; else
    // the field's base colour. Non-select cells always fall back to the base.
    function fieldFill(f) {
      if (!f) return null
      if (f.type === 'select') {
        const v = fieldVal(f)
        if (v) {
          const opt = optionsOf(f).find(o => o.value === v)
          if (opt && opt.color) return opt.color
        }
        // If nothing picked, don't fill — an empty status shouldn't shout.
        return v ? (f.color || null) : null
      }
      // Only fill text/number/date once the cell has a value — an empty column
      // shouldn't paint over the whole row.
      return fieldVal(f) !== '' ? (f.color || null) : null
    }
    function fieldCellStyle(f) {
      const c = fieldFill(f)
      if (!c) return null
      const ink = inkOn(c)
      return { '--field-fill': c, '--field-ink': ink, '--field-caret': caretSvg(ink) }
    }
    // Same chevron as select.form-control, stroked in the cell's ink colour.
    function caretSvg(ink) {
      const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='${ink}' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>`
      return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`
    }
    function inkOn(hex) {
      if (!/^#[0-9a-fA-F]{6}$/.test(hex || '')) return '#0F1420'
      const r = parseInt(hex.slice(1, 3), 16)
      const g = parseInt(hex.slice(3, 5), 16)
      const b = parseInt(hex.slice(5, 7), 16)
      return (r * 299 + g * 587 + b * 114) / 1000 > 175 ? '#0F1420' : '#FFFFFF'
    }

    return {
      isExternal, isManager, isInternal, urgencyColor, statusLabel, isOverdue, formatDate,
      onTimerUpdate, t, initialsOf,
      assigneeList, assigneeTitle, selectedIds,
      assignOpen, toggleAssign, toggleAssignUser,
      fieldVal, onFieldChange, fieldOf, optionsOf, fieldFill, fieldCellStyle,
      confirmDelete,
    }
  },
}
</script>

<style scoped>
.task-card-wrap { display: flex; flex-direction: column; }

/* The whole row is the click target — it opens the detail/edit drawer. */
.task-row.clickable { cursor: pointer; }
.task-row.clickable:focus-visible { outline: 2px solid var(--accent); outline-offset: -2px; }

/* ── Table row (Tasks board) — aligned columns set by --tt-cols on the group. ── */
.task-row.tbl {
  display: grid;
  grid-template-columns: var(--tt-cols, 1fr 150px 116px 64px 138px);
  /* The base .task-row (flat row) sets gap:12px; as a grid that becomes a 12px
     COLUMN gap, pushing every cell right of its header. The header has no gap, so
     they drifted 12px per column. Zero it so columns sit flush under the headers. */
  gap: 0;
  align-items: stretch;
  background: var(--surface);
  border: none;
  border-bottom: 1px solid var(--row-line);
  border-radius: 0;
  min-height: 0;
  padding: 0;
  overflow: visible;
  cursor: pointer;
}
.task-row.tbl:hover { background: var(--accent-wash); }
/* Delete X — always visible before the title, quiet muted glyph; red on hover. */
.tc-del {
  flex-shrink: 0; border: none; background: none; cursor: pointer;
  color: var(--muted); width: 22px; height: 22px; padding: 0;
  display: inline-flex; align-items: center; justify-content: center;
  border-radius: 6px; transition: color .12s, background .12s;
}
.tc-del:hover { color: var(--danger); background: var(--st-red-bg); }
.task-row.tbl:focus-visible { outline: 2px solid var(--accent); outline-offset: -2px; }
.tc { display: flex; align-items: center; gap: 8px; padding: 9px 14px; min-width: 0; }
.tc-subject { gap: 9px; }
.tc-title { font-size: 15.5px; font-weight: 400; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.task-row.tbl.done .tc-title { text-decoration: line-through; color: var(--muted); }
.tc-sub {
  font-family: var(--font-mono); font-size: 10.5px; font-weight: 700; flex-shrink: 0;
  color: #6E7CD8; background: color-mix(in srgb, #6E7CD8 14%, transparent);
  border-radius: 6px; padding: 1px 7px;
}
.tc-lock { color: var(--faint); flex-shrink: 0; }
.tc-due { font-family: var(--font-mono); font-size: 10.5px; color: var(--faint); margin-inline-start: auto; }
.tc-due.overdue { color: var(--danger); }
/* Time column gets a soft highlight like the reference. */
.tc-time { justify-content: flex-start; background: color-mix(in srgb, #6E7CD8 6%, transparent); }
.tc-urgency .u-pill { font-size: 10.5px; padding: 2px 10px; border-radius: 20px; font-weight: 600; white-space: nowrap; }
.u-low      { background: color-mix(in srgb, var(--low) 16%, transparent);      color: var(--low); }
.u-normal   { background: color-mix(in srgb, var(--normal) 16%, transparent);   color: var(--normal); }
.u-high     { background: color-mix(in srgb, var(--high) 16%, transparent);     color: var(--high); }
.u-critical { background: color-mix(in srgb, var(--critical) 16%, transparent); color: var(--critical); }
.tc-action { justify-content: flex-end; }
.tc-field { justify-content: stretch; }

/* Monday-style filled cell — the picked colour paints the whole cell area, and
   the inner input goes transparent so the fill reads edge-to-edge. Text flips to
   a contrast-safe ink derived from the fill luminance. */
.tc-field.has-fill {
  background: var(--field-fill);
  color: var(--field-ink);
  padding: 0; /* input handles its own padding so the fill runs corner to corner */
}
.tc-field.has-fill .field-input {
  background: transparent;
  color: var(--field-ink);
  box-shadow: none;
  border-radius: 0;
  padding: 9px 14px;
  font-weight: 600;
}
.tc-field.has-fill .field-input:hover { box-shadow: inset 0 0 0 2px rgba(255,255,255,.35); }
.tc-field.has-fill .field-input:focus { box-shadow: inset 0 0 0 2px rgba(255,255,255,.6); outline: none; }
/* Replace the native marker with one chevron in the fill's ink (set inline as
   --field-caret). Caret side flips for RTL. */
.tc-field.has-fill select.field-input {
  appearance: none; -webkit-appearance: none; -moz-appearance: none;
  background-image: var(--field-caret);
  background-position: right 12px center;
  background-repeat: no-repeat;
  padding-inline-end: 30px;
}
html[dir="rtl"] .tc-field.has-fill select.field-input {
  background-position: left 12px center;
}
/* Borders are transparent in the design system, so the input reads via a filled
   surface + a themed inset edge (dark hairline on light, light hairline on dark). */
.field-input {
  width: 100%; min-width: 0; box-sizing: border-box;
  background: var(--surface-2); color: var(--text);
  border: 1px solid transparent; border-radius: 8px; padding: 5px 8px;
  box-shadow: inset 0 0 0 1px rgba(20,24,40,.14);
  font: inherit; font-size: 13px;
  transition: box-shadow .12s;
}
[data-theme="dark"] .field-input { box-shadow: inset 0 0 0 1px rgba(255,255,255,.16); }
.field-input:hover { box-shadow: inset 0 0 0 1px rgba(20,24,40,.24); }
[data-theme="dark"] .field-input:hover { box-shadow: inset 0 0 0 1px rgba(255,255,255,.26); }
.field-input:focus { outline: none; box-shadow: inset 0 0 0 1.5px var(--accent); }
.field-select { cursor: pointer; }
/* The open dropdown's options render on a system-white popup and inherit the
   select's text colour. On a filled cell that colour is white ink → white-on-white
   invisible options. Pin options to theme surface + text so they stay readable. */
.field-select option {
  color: var(--text);
  background: var(--surface);
}
.done-pill {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 12px; font-weight: 600; color: var(--muted);
  background: var(--surface-2); border: 1px solid var(--border-2);
  border-radius: 20px; padding: 5px 12px; cursor: pointer; white-space: nowrap;
}
.done-pill:hover { color: var(--success); border-color: var(--success); background: color-mix(in srgb, var(--success) 8%, transparent); }
.done-circle { width: 13px; height: 13px; border-radius: 50%; border: 2px solid currentColor; flex-shrink: 0; }
.done-pill.restore:hover { color: var(--accent-ink); border-color: var(--accent-ink); background: var(--accent-wash); }

/* Dense task row — hairline-divided, flat. Urgency shown as an edge tick. */
.task-row {
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--surface);
  border: 1.5px solid var(--card-brd);
  border-radius: var(--radius);
  padding: 0 14px 0 0;
  min-height: 54px;
  transition: background .12s, border-color .12s;
  position: relative;
  overflow: hidden;
}
.task-row:hover { background: var(--accent-wash); border-color: var(--border-2); }
.task-row.done  { opacity: .6; }

.row-tick { width: 3px; align-self: stretch; flex-shrink: 0; background: var(--low); }
.urgency-normal   .row-tick { background: var(--normal); }
.urgency-high     .row-tick { background: var(--high); }
.urgency-critical .row-tick { background: var(--critical); }

/* Title block no longer fills the row — it caps so the action buttons sit right
   next to the title; a flexible spacer then pushes status + assignee to the end. */
.row-main { flex: 0 1 auto; min-width: 0; max-width: min(46%, 460px); padding-inline-start: 12px; }
.row-fill { flex: 1 1 auto; }
.row-title {
  font-size: 15px; font-weight: 600; line-height: 1.3;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.task-row.done .row-title { text-decoration: line-through; }
.row-meta {
  font-family: var(--font-mono);
  font-size: 10.5px;
  color: var(--faint);
  margin-top: 2px;
  display: flex; align-items: center; gap: 6px; flex-wrap: wrap;
}
.row-meta .sep { opacity: .6; }
.row-meta .proj { font-weight: 600; }
.row-meta .priv { display: inline-flex; align-items: center; gap: 3px; }
.row-meta .overdue { color: var(--danger); }

.row-ini {
  width: 26px; height: 26px; flex-shrink: 0;
  border-radius: 7px;
  background: var(--surface-2); border: 1px solid var(--border-2);
  color: var(--muted);
  display: grid; place-items: center;
  font-size: 10.5px; font-weight: 600;
}

/* ── Inline quick-assign ── */
.row-assign { position: relative; flex-shrink: 0; }
.assign-btn {
  width: 28px; height: 28px;
  border-radius: 8px;
  border: 1px solid var(--border-2);
  background: var(--surface-2);
  color: var(--muted);
  display: grid; place-items: center;
  font-size: 10.5px; font-weight: 600;
  cursor: pointer;
  transition: border-color .12s, color .12s, background .12s;
}
.assign-btn:hover { border-color: var(--accent-ink); color: var(--accent-ink); background: var(--accent-wash); }
.assign-btn.unassigned { border-style: dashed; }
/* When an avatar stack is shown the button grows to fit the stack. */
.assign-btn.has-stack { width: auto; min-width: 28px; padding: 0 3px; border-color: transparent; background: none; }
.assign-btn.has-stack:hover { background: var(--accent-wash); }

/* Overlapping avatar stack (assignees), newest tucked under the previous. */
.avatar-stack { display: inline-flex; flex-direction: row; align-items: center; }
.avatar-stack .av {
  width: 26px; height: 26px; flex-shrink: 0;
  border-radius: 7px;
  background: var(--surface-2);
  box-shadow: 0 0 0 2px var(--surface);
  color: var(--muted);
  display: grid; place-items: center;
  font-size: 10.5px; font-weight: 600;
}
.avatar-stack .av + .av { margin-inline-start: -8px; }
.avatar-stack .av-more { background: var(--accent-soft); color: var(--accent-ink); }

/* Centered modal box (teleported to <body>) — robustly on top, never clipped. */
.assign-modal-root {
  position: fixed; inset: 0; z-index: 1200;
  display: grid; place-items: center;
  background: rgba(16,22,40,.42);
  padding: 20px;
  animation: assign-fade .15s ease;
}
@keyframes assign-fade { from { opacity: 0; } to { opacity: 1; } }
.assign-box {
  width: min(340px, 100%);
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 16px;
  box-shadow: 0 18px 48px rgba(16,22,40,.28);
  overflow: hidden;
  animation: assign-pop .16s ease;
}
@keyframes assign-pop { from { transform: scale(.96); opacity: .6; } to { transform: none; opacity: 1; } }
.assign-box-head {
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  padding: 14px 16px; border-bottom: 1px solid var(--border);
}
.assign-box-title { font-size: 15px; font-weight: 700; color: var(--text); }
.assign-x {
  border: none; background: none; cursor: pointer; color: var(--muted);
  display: inline-flex; padding: 4px; border-radius: 8px;
}
.assign-x:hover { color: var(--text); background: var(--hover); }
.assign-box-body { padding: 8px; display: flex; flex-direction: column; gap: 2px; max-height: 60vh; overflow-y: auto; }
.assign-opt {
  display: flex; align-items: center; gap: 11px;
  width: 100%; text-align: start;
  padding: 10px 11px; border: none; background: none; cursor: pointer;
  border-radius: 10px; font-size: 14px; color: var(--text);
}
.assign-opt:hover { background: var(--hover); }
.assign-opt.active { background: var(--accent-wash); color: var(--accent-ink); font-weight: 600; }
.assign-opt .ao-check { margin-inline-start: auto; color: var(--accent-ink); flex-shrink: 0; }
.assign-opt.unassign { color: var(--muted); border-top: 1px solid var(--border); border-radius: 0; margin-top: 6px; padding-top: 12px; justify-content: center; }
.ao-ini {
  width: 30px; height: 30px; flex-shrink: 0; border-radius: 8px;
  background: var(--surface-2); border: 1px solid var(--border-2);
  display: grid; place-items: center; font-size: 12px; font-weight: 700; color: var(--muted);
}
.ao-name { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ao-empty { font-size: 13px; color: var(--faint); padding: 12px; text-align: center; }

.row-actions { display: flex; align-items: center; gap: 5px; flex-shrink: 0; }
/* Action buttons read as bold, accent-colored chips in both themes (was faint grey). */
.btn-icon {
  color: var(--accent-ink);
  width: 30px; height: 30px;
  padding: 0;
  justify-content: center;
  background: var(--accent-wash);
  border: 1px solid color-mix(in srgb, var(--accent-ink) 28%, transparent);
  border-radius: 8px;
}
.btn-icon svg { width: 15px; height: 15px; stroke: currentColor; stroke-width: 2.4; }
.btn-icon:hover { color: var(--on-accent); background: var(--accent); border-color: var(--accent); }
.danger-icon {
  color: var(--danger);
  background: color-mix(in srgb, var(--danger) 10%, transparent);
  border-color: color-mix(in srgb, var(--danger) 26%, transparent);
}
.danger-icon:hover { color: #fff; background: var(--danger); border-color: var(--danger); }

/* Mobile: let the row wrap so the title gets a full line instead of truncating.
   Urgency moves from the side tick to a colored inline-start border. */
/* Stacked layout: shared by the mobile breakpoint and the compact (widget) flag.
   The row wraps, the title gets a full line, and actions drop below it. */
.compact .task-row,
.compact .task-row.urgency-normal,
.compact .task-row.urgency-high,
.compact .task-row.urgency-critical { flex-wrap: wrap; min-height: 0; padding: 11px 13px; gap: 8px 10px; }
.compact .task-row { border-inline-start: 3px solid var(--low); }
.compact .task-row.urgency-normal   { border-inline-start-color: var(--normal); }
.compact .task-row.urgency-high     { border-inline-start-color: var(--high); }
.compact .task-row.urgency-critical { border-inline-start-color: var(--critical); }
.compact .row-tick { display: none; }
.compact .row-main { flex: 1 1 100%; max-width: none; padding-inline-start: 0; }
.compact .row-title { white-space: normal; overflow: visible; }
.compact .row-fill { display: none; }
.compact .row-actions { flex: 1 1 100%; flex-wrap: wrap; }

@media (max-width: 768px) {
  .task-row { flex-wrap: wrap; min-height: 0; padding: 11px 13px; gap: 8px 10px; border-inline-start: 3px solid var(--low); }
  .task-row.urgency-normal   { border-inline-start-color: var(--normal); }
  .task-row.urgency-high     { border-inline-start-color: var(--high); }
  .task-row.urgency-critical { border-inline-start-color: var(--critical); }
  .row-tick { display: none; }
  .row-main { flex: 1 1 100%; max-width: none; padding-inline-start: 0; }
  .row-title { white-space: normal; overflow: visible; }
  .row-fill { display: none; }
  .row-actions { margin-inline-start: auto; }
}
@media (max-width: 560px) {
  .row-actions { gap: 2px; }
  .row-meta { font-size: 10px; }
}
</style>
